import { MS_IN_HOUR, MS_IN_MINUTE } from '../../utils/time-utils';
import RandomDataGenerator from '../../libs/RandomDataGenerator';
import type { OpponentEntry, RaceEventConfig, RaceEventState } from './types';
import { getFakeUsers } from '../../app/utils/getFakeUsers';
import type { LeaderboardEntry, PlatformUser } from '../../app/platform/Platform';
import type { RaceEventObservable } from './RaceEventController';

const competitorsConfig = [
  { time: 0.5, variance: 0.1 },
  { time: 0.75, variance: 0.1 },
  { time: 1, variance: 0 },
  { time: 1.5, variance: 0.2 },
];

const competitorMinIncreaseInterval = MS_IN_MINUTE * 5;

const rnd = new RandomDataGenerator([Date.now()]);

function splitPeriod(total: number, chunks: number, minPeriod: number): number[] {
  let min = minPeriod;
  if (total < chunks * min) {
    console.warn('RaceEventUtils - min period needs to be shorten');
    min = total / chunks;
  }
  const remainder = total - chunks * min;
  const cuts: number[] = [];
  for (let i = 0; i < chunks - 1; i += 1) {
    cuts.push(Math.floor(Math.random() * (remainder + 1)));
  }
  cuts.sort((a, b) => a - b);
  const result: number[] = [];
  let prev = 0;
  // eslint-disable-next-line no-restricted-syntax
  for (const cut of cuts) {
    result.push(cut - prev + min);
    prev = cut;
  }
  result.push(remainder - prev + min);

  let sum = 0;
  return result.map((period) => {
    const newValue = period + sum;
    sum += period;
    return newValue;
  });
}

export function generateRaceEventOpponents(config: RaceEventConfig): OpponentEntry[] {
  const { levelsToBeat, durationHours } = config;

  const users = getFakeUsers(competitorsConfig.length);

  return rnd.shuffle(
    competitorsConfig.map(({ time, variance }, index) => {
      const timeToAct = (time + rnd.realInRange(-variance, variance)) * (durationHours * MS_IN_HOUR);
      return {
        ...users[index],
        finishedRank: null,
        increaseTime: splitPeriod(timeToAct, levelsToBeat, competitorMinIncreaseInterval).filter(
          (value) => value <= durationHours * MS_IN_HOUR,
        ),
      };
    }),
  );
}

export function getOpponentProgress(state: RaceEventState, opponent: OpponentEntry, time: number = Date.now()): number {
  if (state.startDate === null) return 0;
  if (opponent.finishedRank !== null) return 1;
  const timePassed = time - state.startDate;
  return opponent.increaseTime.filter((timeDiff) => timeDiff < timePassed).length;
}

export function updateOpponents(observable: RaceEventObservable): void {
  const { raceEvent } = observable.getValue();
  if (!raceEvent) return;
  if (raceEvent.finished) return;

  let availableRank = raceEvent.opponents.filter((opponent) => opponent.finishedRank !== null).length + 1;

  observable.updateValue({
    raceEvent: {
      ...raceEvent,
      opponents: raceEvent.opponents.map((opponent) => {
        if (opponent.finishedRank) return opponent;
        if (getOpponentProgress(raceEvent, opponent) >= raceEvent.config.levelsToBeat) {
          opponent.finishedRank = availableRank;
          availableRank += 1;
        }
        return opponent;
      }),
    },
  });
}

export function getRaceLastSeenLeaderboard(state: RaceEventState, player?: PlatformUser): LeaderboardEntry[] {
  const { opponents, lastSeenLeaderboard } = state;

  return [
    {
      player: {
        id: player?.id ?? '',
        avatarUrl: player?.avatarUrl ?? '',
        name: player?.name ?? '',
        currentUser: true,
      },
      rank: lastSeenLeaderboard?.current?.rank ?? 1,
      score: lastSeenLeaderboard?.current?.score ?? 0,
    },
    ...opponents.map((opponent, index) => ({
      player: opponent,
      rank: lastSeenLeaderboard?.[opponent.id]?.rank ?? index + 2,
      score: lastSeenLeaderboard?.[opponent.id]?.score ?? 0,
    })),
  ].sort((a, b) => a.rank - b.rank);
}

export function getRaceLeaderboard(state: RaceEventState, player?: PlatformUser): LeaderboardEntry[] {
  const playerLevel = state.currentProgress;
  const time = Date.now();

  const finishedOpponents: LeaderboardEntry[] = state.opponents
    .filter((opponent) => opponent.finishedRank !== null)
    .map((opponent) => ({
      player: opponent,
      rank: opponent.finishedRank ?? 1,
      score: state.config.levelsToBeat,
    }));

  const leaderboard: LeaderboardEntry[] = [
    {
      player: {
        id: player?.id ?? '',
        avatarUrl: player?.avatarUrl ?? '',
        name: player?.name ?? '',
        currentUser: true,
      },
      rank: 0,
      score: playerLevel,
    },
    ...state.opponents
      .filter((opponent) => opponent.finishedRank === null)
      .map((opponent) => ({
        player: opponent,
        rank: opponent.finishedRank ?? Infinity,
        score: getOpponentProgress(state, opponent, time),
      })),
  ].sort((a, b) => {
    if (b.score !== a.score) return b.score - a.score;
    if (a.player.currentUser && !b.player.currentUser) return -1;
    if (!a.player.currentUser && b.player.currentUser) return 1;
    return 0;
  });

  leaderboard.forEach((entry, index): void => {
    entry.rank = finishedOpponents.length + index + 1;
  });

  return [...finishedOpponents, ...leaderboard];
}
